import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
    Box,
    Checkbox,
    CircularProgress,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import BackNavigationButton from '../../components/common/BackNavigationButton';
import DialogAdaptive from '../../components/common/DialogAdaptive';
import DialogContentAdaptive from '../../components/common/DialogContentAdaptive';
import PageContainer from '../../components/common/PageContainter';
import StyledCardTitleBar from '../../components/common/StyledCardTitleBar';
import VaultBulkTagDialog from '../../components/vault/VaultBulkTagDialog';
import VaultMenu from '../../components/vault/VaultMenu';
import useSubscriber from '../../hooks/useSubscriber';
import useSubscriberVaultAlbums, { OnlyFansVaultAlbums } from '../../hooks/useSubscriberVaultAlbums';
import { VaultMediaResponseList } from '../../hooks/useSubscriberVaultMedias';
import { SettingsContext } from '../../store/SettingsContext';

const VaultPage = () => {
    const settingsContext = useContext(SettingsContext);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const { data: subscriber } = useSubscriber();

    const [selectedItems, setSelectedItems] = useState<VaultMediaResponseList[]>([]);
    const [selectedAlbum, setSelectedAlbum] = useState<number>(0);
    const [openAlbumScreen, setOpenAlbumScreen] = useState<boolean>(false);
    const [openBulkTagDialog, setOpenBulkTagDialog] = useState<boolean>(false);
    const [sortAlbums, setSortAlbums] = useState<boolean>(true);
    const [refreshContent, setRefreshContent] = useState<boolean>(false);

    const {
        data: vaultAlbums,
        fetchNextPage: fetchNextPageAlbums,
        hasNextPage: vaultAlbumHasNextPage,
        isFetchingNextPage: vaultAlbumIsFetchingNextPage,
        refetch: refetchAlbums,
        isLoading: vaultAlbumsLoading,
        isRefetching: vaultAlbumIsRefetching,
        clearCacheForVaultAlbumRefresh,
        addToVaultAlbum,
        removeFromVaultAlbum,
    } = useSubscriberVaultAlbums(true);

    const processedAlbums = useMemo(() => {
        if (!vaultAlbums?.pages) return [];

        const allAlbums = vaultAlbums.pages.flatMap(page => page.list);

        if (!sortAlbums) return allAlbums;

        return [...allAlbums].sort((a, b) => a.name.localeCompare(b.name));
    }, [vaultAlbums, sortAlbums]);


    const mediaSelected = useCallback((item: VaultMediaResponseList) => {
        setSelectedItems(prev => {
            const exists = prev.some(i => i.id === item.id);

            if (exists) {
                // remove from array
                return prev.filter(i => i.id !== item.id);
            }
            // add to array
            return [...prev, item];
        });
    }, []);

    const selectAllItems = (items: VaultMediaResponseList[]) => {
        setSelectedItems(items);
    };
    const deSelectAllItems = () => {
        setSelectedItems([]);
    };
    const handleAlbumToggle = async (value: OnlyFansVaultAlbums.List, isCurrentlyChecked: boolean) => {
        if (isCurrentlyChecked) {
            // run remove items
            const result = await removeFromVaultAlbum(
                value.id,
                selectedItems.map(mp => mp.id),
            );
            if (result) {
                // success!
                setSelectedItems([]);
                setOpenAlbumScreen(false);
                setRefreshContent(!refreshContent);
                enqueueSnackbar('Media Removed Successfully!', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            } else {
                // failure!
                enqueueSnackbar("Wasn't able to move content, please try again!", {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            }
        } else {
            // run add items
            const result = await addToVaultAlbum(
                value.id,
                selectedItems.map(mp => mp.id),
            );
            if (result) {
                // success!
                setSelectedItems([]);
                setOpenAlbumScreen(false);
                enqueueSnackbar('Media Added Successfully!', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            } else {
                // failure!
                enqueueSnackbar("Wasn't able to add content, please try again!", {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            }
        }
    };

    useEffect(() => {
        // when switching categories/albums don't allow carryover of selected items.
        setSelectedItems([]);
    }, [selectedAlbum]);

    const showLoading = vaultAlbumsLoading || vaultAlbumIsRefetching;

    return (
        <PageContainer sx={{ pb: 0 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
                <BackNavigationButton url={`/subscribers/${subscriber?._id}/${settingsContext.services.inventory.homeUrl}`} /> Vault for{' '}
                {subscriber?.username}
            </Typography>

            <Grid container spacing={2}>
                <Grid item display={fullScreen ? '' : 'flex'} justifyContent={fullScreen ? '' : 'center'} xs={12}>
                    {subscriber && (
                        <VaultMenu
                            subscriber={subscriber}
                            editOnly={false}
                            selectedItems={selectedItems}
                            onItemClick={mediaSelected}
                            selectAllItems={selectAllItems}
                            clearAllItems={deSelectAllItems}
                            setSelectedAlbum={setSelectedAlbum}
                            refreshContent={refreshContent}
                            selectionActions={
                                <>
                                    <Tooltip title="Add/Remove to Vault Album">
                                        <IconButton color="primary" onClick={() => setOpenAlbumScreen(true)} size="large">
                                            <AddCircleOutlineIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Tag Creators / Release Forms">
                                        <IconButton color="primary" onClick={() => setOpenBulkTagDialog(true)} size="large">
                                            <LocalOfferIcon />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            }
                        />
                    )}
                </Grid>
            </Grid>
            {subscriber && (
                <VaultBulkTagDialog
                    open={openBulkTagDialog}
                    onClose={() => setOpenBulkTagDialog(false)}
                    subscriber={subscriber}
                    items={selectedItems}
                    onCompleted={() => setSelectedItems([])}
                />
            )}
            <DialogAdaptive open={openAlbumScreen} onClose={() => setOpenAlbumScreen(false)}>
                <StyledCardTitleBar
                    title="Add/Remove to Vault Album"
                    badge={
                        <IconButton color="warning" onClick={() => setOpenAlbumScreen(false)}>
                            <CloseIcon />
                        </IconButton>
                    }
                />
                <DialogContentAdaptive>
                    <Box>
                        {showLoading && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    py: 2,
                                }}
                            >
                                <CircularProgress size={24} />
                            </Box>
                        )}

                        <List dense>
                            {processedAlbums.map(album => {
                                const checked = album.id === selectedAlbum;

                                return (
                                    <ListItem key={album.id} disablePadding>
                                        <Checkbox
                                            edge="start"
                                            checked={checked}
                                            icon={<RadioButtonUncheckedIcon />}
                                            checkedIcon={<CheckCircleIcon />}
                                            onClick={() => handleAlbumToggle(album, checked)}
                                        />
                                        <ListItemButton onClick={() => handleAlbumToggle(album, checked)}>
                                            <ListItemText primary={album.name} />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Box>
                </DialogContentAdaptive>
            </DialogAdaptive>
        </PageContainer>
    );
};

export default VaultPage;
